function canonicalId(value) {
  if (!value) return null;
  if (typeof value === "string") return value;
  return value.id || null;
}

function visibleWellBoxId(value) {
  if (!value || value === "hide") return null;
  return canonicalId(value);
}

function wellKey(puckId, wellNo) {
  return `${puckId}:${wellNo}`;
}

function cloneWithReadOnly(entity, readOnly) {
  if (!entity || typeof entity !== "object") {
    return entity;
  }

  if (readOnly === undefined) {
    return { ...entity };
  }

  return { ...entity, readOnly: !!readOnly };
}

function createSnapshot() {
  return {
    labs: [],
    dewars: new Map(),
    racks: new Map(),
    pucks: new Map(),
    boxes: new Map(),
    wells: new Map(),
  };
}

function buildSnapshotFromNestedTree(serverTree) {
  const snapshot = createSnapshot();

  (serverTree || []).forEach((labGroup) => {
    const labEntry = {
      lab: labGroup?.lab || "My Lab",
      labId: canonicalId(labGroup?.labId),
      dewars: [],
    };

    (labGroup?.data || []).forEach((dewar) => {
      const dewarId = canonicalId(dewar);
      if (!dewarId) return;

      const rackIds = [];
      snapshot.dewars.set(dewarId, {
        readOnly: !!dewar?.readOnly,
        lab: labEntry.lab,
        labId: labEntry.labId,
        racks: rackIds,
      });
      labEntry.dewars.push(dewarId);

      (dewar?.racks || []).forEach((rack) => {
        const rackId = canonicalId(rack);
        if (!rackId) return;

        const puckIds = [];
        snapshot.racks.set(rackId, {
          readOnly: !!rack?.readOnly,
          dewarId,
          pucks: puckIds,
        });
        rackIds.push(rackId);

        (rack?.pucks || []).forEach((puck) => {
          const puckId = canonicalId(puck);
          if (!puckId) return;

          puckIds.push(puckId);
          snapshot.pucks.set(puckId, {
            readOnly: !!puck?.readOnly,
            rackId,
          });

          for (let wellNo = 1; wellNo <= 12; wellNo += 1) {
            const wellValue = puck?.[`well${wellNo}`];
            const key = wellKey(puckId, wellNo);

            if (wellValue === "hide") {
              snapshot.wells.set(key, { state: "hide" });
              continue;
            }

            const boxId = canonicalId(wellValue);
            if (!boxId) {
              continue;
            }

            snapshot.boxes.set(boxId, {
              readOnly: !!wellValue?.readOnly,
              puckId,
              wellNo,
            });
            snapshot.wells.set(key, {
              state: "visible",
              boxId,
            });
          }
        });
      });
    });

    snapshot.labs.push(labEntry);
  });

  return snapshot;
}

function buildSnapshotFromFlatPermissions(permissionPayload) {
  const snapshot = createSnapshot();
  const labs = Array.isArray(permissionPayload?.labs) ? permissionPayload.labs : [];

  labs.forEach((lab) => {
    snapshot.labs.push({
      lab: lab?.lab || "My Lab",
      labId: canonicalId(lab?.labId),
      dewars: Array.isArray(lab?.dewars) ? lab.dewars.filter(Boolean) : [],
    });
  });

  Object.entries(permissionPayload?.dewars || {}).forEach(([id, entry]) => {
    snapshot.dewars.set(id, {
      readOnly: !!entry?.readOnly,
      lab: entry?.lab || "My Lab",
      labId: canonicalId(entry?.labId),
      racks: Array.isArray(entry?.racks) ? entry.racks.filter(Boolean) : [],
    });
  });

  Object.entries(permissionPayload?.racks || {}).forEach(([id, entry]) => {
    snapshot.racks.set(id, {
      readOnly: !!entry?.readOnly,
      dewarId: canonicalId(entry?.dewarId),
      pucks: Array.isArray(entry?.pucks) ? entry.pucks.filter(Boolean) : [],
    });
  });

  Object.entries(permissionPayload?.pucks || {}).forEach(([id, entry]) => {
    snapshot.pucks.set(id, {
      readOnly: !!entry?.readOnly,
      rackId: canonicalId(entry?.rackId),
    });
  });

  Object.entries(permissionPayload?.boxes || {}).forEach(([id, entry]) => {
    snapshot.boxes.set(id, {
      readOnly: !!entry?.readOnly,
      puckId: canonicalId(entry?.puckId),
      wellNo: Number(entry?.wellNo),
    });
  });

  Object.entries(permissionPayload?.wells || {}).forEach(([id, entry]) => {
    if (entry?.state === "hide") {
      snapshot.wells.set(id, { state: "hide" });
      return;
    }

    if (entry?.state === "visible") {
      snapshot.wells.set(id, {
        state: "visible",
        boxId: entry?.boxId ? canonicalId(entry.boxId) : null,
      });
    }
  });

  return snapshot;
}

function buildPermissionSnapshot(permissionSource) {
  if (Array.isArray(permissionSource)) {
    return buildSnapshotFromNestedTree(permissionSource);
  }

  if (permissionSource && typeof permissionSource === "object") {
    return buildSnapshotFromFlatPermissions(permissionSource);
  }

  return createSnapshot();
}

function indexLegacyTree(liveTree) {
  const index = {
    dewars: new Map(),
    racks: new Map(),
    pucks: new Map(),
    boxes: new Map(),
  };

  (liveTree || []).forEach((labGroup) => {
    (labGroup?.data || []).forEach((dewar) => {
      const dewarId = canonicalId(dewar);
      if (!dewarId) return;
      index.dewars.set(dewarId, dewar);

      (dewar?.racks || []).forEach((rack) => {
        const rackId = canonicalId(rack);
        if (!rackId) return;
        index.racks.set(rackId, rack);

        (rack?.pucks || []).forEach((puck) => {
          const puckId = canonicalId(puck);
          if (!puckId) return;
          index.pucks.set(puckId, puck);

          for (let wellNo = 1; wellNo <= 12; wellNo += 1) {
            const box = puck?.[`well${wellNo}`];
            const boxId = visibleWellBoxId(box);
            if (!boxId) continue;
            index.boxes.set(boxId, box);
          }
        });
      });
    });
  });

  return index;
}

function mergeLiveInventoryWithPermissions(
  liveTree,
  permissionSource,
  fallbackTree = []
) {
  const permissionSnapshot = buildPermissionSnapshot(permissionSource);
  const hasPermissions =
    permissionSnapshot.labs.length > 0 || permissionSnapshot.dewars.size > 0;

  if (!hasPermissions) {
    const liveHasData = (liveTree || []).some(
      (lab) => Array.isArray(lab?.data) && lab.data.length > 0
    );
    if (liveHasData) return liveTree;
    return Array.isArray(fallbackTree) && fallbackTree.length ? fallbackTree : liveTree;
  }

  const liveIndex = indexLegacyTree(liveTree);
  const fallbackIndex = indexLegacyTree(fallbackTree);

  return permissionSnapshot.labs.map((labEntry) => ({
    lab: labEntry.lab,
    labId: labEntry.labId,
    data: labEntry.dewars
      .map((dewarId) => {
        const dewarPermission = permissionSnapshot.dewars.get(dewarId);
        if (!dewarPermission) return null;

        const liveDewar =
          liveIndex.dewars.get(dewarId) || fallbackIndex.dewars.get(dewarId);
        if (!liveDewar) return null;

        const dewar = cloneWithReadOnly(liveDewar, dewarPermission.readOnly);

        dewar.racks = (dewarPermission.racks || [])
          .map((rackId) => {
            const rackPermission = permissionSnapshot.racks.get(rackId);
            if (!rackPermission) return null;

            const liveRack =
              liveIndex.racks.get(rackId) || fallbackIndex.racks.get(rackId);
            if (!liveRack) return null;

            const rack = cloneWithReadOnly(liveRack, rackPermission.readOnly);

            rack.pucks = (rackPermission.pucks || [])
              .map((puckId) => {
                const puckPermission = permissionSnapshot.pucks.get(puckId);
                if (!puckPermission) return null;

                const livePuck =
                  liveIndex.pucks.get(puckId) || fallbackIndex.pucks.get(puckId);
                if (!livePuck) return null;

                const puck = cloneWithReadOnly(livePuck, puckPermission.readOnly);

                for (let wellNo = 1; wellNo <= 12; wellNo += 1) {
                  const key = wellKey(puckId, wellNo);
                  const wellPermission = permissionSnapshot.wells.get(key);
                  const visibleBoxId =
                    wellPermission?.boxId ||
                    visibleWellBoxId(puck?.[`well${wellNo}`]) ||
                    visibleWellBoxId(livePuck?.[`well${wellNo}`]);

                  if (wellPermission?.state === "hide") {
                    puck[`well${wellNo}`] = "hide";
                    continue;
                  }

                  if (visibleBoxId) {
                    const boxPermission = permissionSnapshot.boxes.get(visibleBoxId);
                    const liveBox =
                      liveIndex.boxes.get(visibleBoxId) ||
                      fallbackIndex.boxes.get(visibleBoxId) ||
                      null;
                    puck[`well${wellNo}`] = liveBox
                      ? cloneWithReadOnly(liveBox, boxPermission?.readOnly)
                      : null;
                    continue;
                  }

                  puck[`well${wellNo}`] = null;
                }

                return puck;
              })
              .filter(Boolean);

            return rack;
          })
          .filter(Boolean);

        return dewar;
      })
      .filter(Boolean),
  }));
}

export { buildPermissionSnapshot, canonicalId, mergeLiveInventoryWithPermissions, wellKey };
